import React, { useState, useEffect } from "react";
import { Route, Routes, useLocation, matchPath } from "react-router-dom";
import "./App.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "./assets/scss/style.scss";

// Component imports
import Login from "./pages/login";
import Dashboard from "./pages/quote-bind-management/dashboard";
import Sidebar from "./components/sidebar";
import ManageEmails from "./pages/quote-bind-management/manage-emails";
import CreateEmail from "./pages/quote-bind-management/create-email";
import ViewEmail from "./pages/quote-bind-management/view-email";
import ViewAuditAccess from "./pages/quote-bind-management/view-audit-access";
import AddAuditAccess from "./pages/quote-bind-management/add-audit-access";
import ViewUserAudit from "./pages/quote-bind-management/user-audit-view";
import UserAuditDetails from "./pages/quote-bind-management/user-audit-details";
import ViewEmailAudit from "./pages/quote-bind-management/email-audit-view";
import EmailAuditDetails from "./pages/quote-bind-management/email-audit-details";
import GcSidebar from "./components/gcSidebar";
import GeographicalConcentration from "./pages/geographical-concentration";
import RenewalsDashboard from "./pages/renewals/index";
import RenewalExpirationDayManagement from "./pages/renewals/settings/expirationdaycount";
import RenewalDataPointsSettings from "./pages/renewals/settings/datapointsettings";
import RenewalStateNonRenewalDaySettings from "./pages/renewals/settings/statenonrenewalsdays";
import RenewalsManageRoles from "./pages/renewals/roles-and-permissions/manageroles";
import RenewalsAddRole from "./pages/renewals/roles-and-permissions/addroles";
import RenewalsAddUsers from "./pages/renewals/roles-and-permissions/addusers";
import RenewalsEditRole from "./pages/renewals/roles-and-permissions/editroles";
import RenewalsLogs from "./pages/renewals/renewals-logs/logs";
import RenewalsPayload from "./pages/renewals/renewals-logs/view";
import RenewalsAddRiskFlag from "./pages/renewals/risk-flagging/add";
import RenewalsListRiskFlag from "./pages/renewals/risk-flagging/list";
import RenewalsNotificationSettings from "./pages/renewals/settings/notificationsettings";
import RenewalsNotificationWidget from "./pages/renewals/notifications/notificationwidget";
import RenewalsSidebar from "./components/renewalsSidebar";

import ApiDocs from "./pages/ApiDocs";

function App() {
  const location = useLocation();
  const isLoginPage = location.pathname === "/";
  const isApiDocsPage = location.pathname === "/api-docs";
  const isGcRoute = location.pathname === "/geographical-concentration";
  const isRenewalsRoute =
    !!matchPath("/renewals-dashboard", location.pathname) ||
    !!matchPath("/renewals-expiration-days", location.pathname) ||
    !!matchPath("/renewals-datapoint-settings", location.pathname) ||
    !!matchPath("/renewals-manage-roles", location.pathname) ||
    !!matchPath("/renewals-add-roles", location.pathname) ||
    !!matchPath("/renewals-add-users-to-role", location.pathname) ||
    !!matchPath("/renewals-edit-role", location.pathname) ||
    !!matchPath("/renewals-logs", location.pathname) ||
    !!matchPath("/renewals-add-risk-flag", location.pathname) ||
    !!matchPath("/renewals-list-risk-flag", location.pathname) ||
    !!matchPath("/renewals-notification-settings", location.pathname) ||
    !!matchPath("/renewals-state-non-renewal-days", location.pathname) ||
    !!matchPath("/renewals-payload/:controlNo", location.pathname);

  const [isSidebarExpanded, setSidebarExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const padding =
    location.pathname === "/geographical-concentration"
      ? "0"
      : "24px";

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleSidebar = () => {
    setSidebarExpanded(!isSidebarExpanded);
  };

  // Render API docs in full screen
  if (isApiDocsPage) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
        }}
      >
        <ApiDocs />
      </div>
    );
  }

  // Render login page without sidebar
  if (isLoginPage) {
    return (
      <div className="App">
        <Login />
      </div>
    );
  }

  // Render main application layout with sidebar
  return (
    <div className="App">
      {isLoginPage ? (
        <Routes>
          <Route path="/" element={<Login />} />
        </Routes>
      ) : (
        <div className="app-container">
          {/* Separate route for API docs */}
          <Routes>
            <Route
              path="/api-docs"
              element={
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                  }}
                >
                  <ApiDocs />
                </div>
              }
            />
          </Routes>

          {/* Only render sidebar and main content if not on API docs page */}
          {window.location.pathname !== "/api-docs" && (
            <>
              {isGcRoute ? (
                <GcSidebar />
              ) : isRenewalsRoute ? (
                <RenewalsSidebar
                  isExpanded={isSidebarExpanded}
                  toggleSidebar={toggleSidebar}
                />
              ) : (
                <Sidebar
                  isExpanded={isSidebarExpanded}
                  toggleSidebar={toggleSidebar}
                />
              )}

              <main
                className={`main-content ${
                  !isMobile && isSidebarExpanded
                    ? isGcRoute
                      ? "ml-gc-sidebar"
                      : isRenewalsRoute
                      ? "ml-renewals-sidebar"
                      : "ml-sidebar"
                    : ""
                }`}
                style={{ padding }}
              >
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/create-email" element={<CreateEmail />} />
                  <Route path="/manage-emails" element={<ManageEmails />} />
                  <Route path="/view-email" element={<ViewEmail />} />
                  <Route path="/view-email/:emailId" element={<ViewEmail />} />
                  <Route
                    path="/view-audit-access"
                    element={<ViewAuditAccess />}
                  />
                  <Route
                    path="/add-audit-access"
                    element={<AddAuditAccess />}
                  />
                  <Route path="/view-user-audit" element={<ViewUserAudit />} />
                  <Route
                    path="/view-email-audit"
                    element={<ViewEmailAudit />}
                  />
                  <Route
                    path="/user-audit-details/:userId"
                    element={<UserAuditDetails />}
                  />
                  <Route
                    path="/email-audit-details/:emailId"
                    element={<EmailAuditDetails />}
                  />
                  <Route
                    path="/geographical-concentration"
                    element={<GeographicalConcentration />}
                  />
                  <Route
                    path="/renewals-dashboard"
                    element={<RenewalsDashboard />}
                  />
                  <Route
                    path="/renewals-expiration-days"
                    element={<RenewalExpirationDayManagement />}
                  />
                  <Route
                    path="/renewals-datapoint-settings"
                    element={<RenewalDataPointsSettings />}
                  />
                  <Route
                    path="/renewals-state-non-renewal-days"
                    element={<RenewalStateNonRenewalDaySettings />}
                  />
                  <Route
                    path="/renewals-notification-settings"
                    element={<RenewalsNotificationSettings />}
                  />
                  <Route
                    path="/renewals-manage-roles"
                    element={<RenewalsManageRoles />}
                  />
                  <Route
                    path="/renewals-add-roles"
                    element={<RenewalsAddRole />}
                  />
                  <Route
                    path="/renewals-add-users-to-role"
                    element={<RenewalsAddUsers />}
                  />
                  <Route
                    path="/renewals-edit-role"
                    element={<RenewalsEditRole />}
                  />
                  <Route
                    path="/renewals-notification-widget"
                    element={<RenewalsNotificationWidget />}
                  />
                  <Route path="/renewals-logs" element={<RenewalsLogs />} />
                  <Route
                    path="/renewals-add-risk-flag"
                    element={<RenewalsAddRiskFlag />}
                  />
                  <Route
                    path="/renewals-list-risk-flag"
                    element={<RenewalsListRiskFlag />}
                  />

                  <Route
                    path="/renewals-payload/:controlNo"
                    element={<RenewalsPayload />}
                  />
                </Routes>
              </main>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
